import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../components/ui/Toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = process.env.REACT_APP_API_URL;

function fmtEur(centimes) {
  return ((centimes || 0) / 100).toFixed(2) + " €";
}

export default function AdminAffiliateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("adminToken");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/admin/affiliates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
      setForm({
        name: d.affiliate.name,
        contactName: d.affiliate.contactName || "",
        email: d.affiliate.email,
        phone: d.affiliate.phone || "",
        commissionRate: d.affiliate.commissionRate,
        status: d.affiliate.status,
        notes: d.affiliate.notes || "",
      });
    } catch {
      toast.error("Affilié introuvable");
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/api/admin/affiliates/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Modifié");
      setEditing(false);
      fetchData();
    } catch {
      toast.error("Erreur sauvegarde");
    }
  };

  const handleMarkAllPaid = async () => {
    try {
      await axios.post(`${API}/api/admin/affiliates/${id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Toutes les commissions marquées payées");
      fetchData();
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-400">Chargement…</div>;
  if (!data) return null;

  const { affiliate, sales } = data;

  // Ventes éligibles uniquement (basé sur le flag backend)
  const eligibleSales = sales.filter((s) => s.isEligibleForCommission);
  const ineligibleSales = sales.filter((s) => !s.isEligibleForCommission);

  // Commissions calculées sur les éligibles seulement
  const unpaidEligibleSales = eligibleSales.filter((s) => !s.commissionPaidAt);
  const totalUnpaid = unpaidEligibleSales.reduce((s, p) => s + p.commission, 0);
  const totalAll = eligibleSales.reduce((s, p) => s + p.commission, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">

        <button onClick={() => navigate("/admin/dashboard")} className="text-xs text-gray-500 hover:text-gray-300">← Dashboard</button>

        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">{affiliate.name}</h1>
            <p className="text-sm text-gray-400 mt-1">
              <span className="font-mono text-indigo-300">{affiliate.couponCode}</span>
              {" · "}
              <span>{(affiliate.commissionRate * 100).toFixed(0)}%</span>
              {" · "}
              <span>{affiliate.status}</span>
            </p>
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm">Modifier</button>
            ) : (
              <>
                <button onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-700 rounded-lg text-sm">Annuler</button>
                <button onClick={handleSave} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm">Sauver</button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            label="Acquisitions"
            value={eligibleSales.length}
            sublabel={ineligibleSales.length > 0 ? `+ ${ineligibleSales.length} doublons` : null}
          />
          <StatCard label="Ventes totales" value={sales.length} />
          <StatCard label="Commission totale" value={fmtEur(totalAll)} highlight="emerald" />
          <StatCard label="À verser" value={fmtEur(totalUnpaid)} highlight="amber" />
        </div>

        {/* Bandeau explicatif si doublons présents */}
        {ineligibleSales.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
            <strong>ℹ️ {ineligibleSales.length} vente(s) non commissionnable(s)</strong> — Le modèle "prime d'acquisition" prévoit une commission unique
            par client référé (sur sa première commande). Les ventes ultérieures du même client n'ouvrent pas droit à commission.
            En théorie cela ne devrait jamais arriver grâce à la restriction Stripe "first-time customer", mais on garde un filet de sécurité côté admin.
          </div>
        )}

        {/* Infos / édition */}
        <Card title="📋 Informations">
          {editing ? (
            <div className="space-y-3">
              <Field label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Contact" value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
              <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Taux (0.10 = 10%)" type="number" value={form.commissionRate} onChange={(v) => setForm({ ...form, commissionRate: parseFloat(v) })} />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Statut</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm">
                  <option value="active">Actif</option>
                  <option value="paused">Pausé</option>
                  <option value="terminated">Terminé</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm" />
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <Row label="Type" value={affiliate.type} />
              <Row label="Contact" value={affiliate.contactName} />
              <Row label="Email" value={affiliate.email} />
              <Row label="Téléphone" value={affiliate.phone} />
              <Row label="Notes" value={affiliate.notes} />
            </div>
          )}
        </Card>

        {/* Ventes */}
        <Card
          title={`💸 Ventes (${sales.length})`}
          action={
            unpaidEligibleSales.length > 0 && (
              <button onClick={handleMarkAllPaid} className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs">
                Marquer tout comme payé
              </button>
            )
          }
        >
          {sales.length === 0 ? (
            <p className="text-xs text-gray-500 italic">Aucune vente.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="text-gray-400 uppercase">
                  <tr>
                    <th className="py-2 px-3 text-left">Date</th>
                    <th className="py-2 px-3 text-left">Entreprise</th>
                    <th className="py-2 px-3 text-left">Type</th>
                    <th className="py-2 px-3 text-right">Pack</th>
                    <th className="py-2 px-3 text-right">Montant</th>
                    <th className="py-2 px-3 text-right">Commission</th>
                    <th className="py-2 px-3 text-left">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => {
                    const isEligible = s.isEligibleForCommission;
                    return (
                      <tr key={s.id} className={`border-t border-white/5 ${!isEligible ? "opacity-60" : ""}`}>
                        <td className="py-2 px-3 text-gray-400">{format(new Date(s.createdAt), "dd/MM/yyyy", { locale: fr })}</td>
                        <td className="py-2 px-3 text-gray-300">{s.company?.name || "—"}</td>
                        <td className="py-2 px-3">
                          {isEligible ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">
                              ✓ Acquisition
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-[10px] font-semibold cursor-help"
                              title={s.ineligibleReason || "Client déjà commissionné"}
                            >
                              ⚪ Doublon
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-400">{s.sessions} × {s.packType}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{fmtEur(s.amountPaid)}</td>
                        <td className="py-2 px-3 text-right">
                          {isEligible ? (
                            <span className="text-emerald-300 font-semibold">{fmtEur(s.commission)}</span>
                          ) : (
                            <span className="text-gray-500 line-through">{fmtEur(s.amountPaid * affiliate.commissionRate)}</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {!isEligible ? (
                            <span className="text-xs text-gray-500">—</span>
                          ) : s.commissionPaidAt ? (
                            <span className="text-xs text-emerald-400">✅ Payé {format(new Date(s.commissionPaidAt), "dd/MM", { locale: fr })}</span>
                          ) : (
                            <span className="text-xs text-amber-400">À verser</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-white/10 flex justify-between items-center">
        <h3 className="text-sm font-semibold">{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value, highlight, sublabel }) {
  const colors = {
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    default: "border-white/10 bg-white/5 text-white",
  };
  return (
    <div className={`rounded-xl p-4 border ${colors[highlight] || colors.default}`}>
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sublabel && <p className="text-xs opacity-60 mt-1">{sublabel}</p>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1 border-b border-white/5">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-200">{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} step={type === "number" ? "0.01" : undefined} className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white" />
    </div>
  );
}